import { BURNOUT, MAPS, WEAPONS, compactColumns, standingsFor, weaponRarity, weaponTraits, weaponType, type Award, type StandingColumn, type StandingPlayer } from "@neon-purge/shared";
import { el } from "../ui/dom.js";
import { fmtClock, fmtStopwatch, ordinal } from "../ui/format.js";
import { drawMapPreview } from "../ui/mapPreview.js";
import { voiceChat } from "./voicechat.js";
import { CH_DEFAULTS, loadBehavior, type Behavior } from "./input.js";

/** Crosshair palette keys → hex, referenced by Behavior.chColor. */
export const CH_COLORS: Record<string, string> = {
  cyan: "#00f0ff",
  green: "#9dff00",
  white: "#ffffff",
  red: "#ff3355",
  magenta: "#ff2ec4",
  yellow: "#ffe000",
  orange: "#ff8a1e",
};

/** Apply the player's crosshair settings to the document root as CSS vars, so
 *  every live crosshair (and the next one built) reflects them. Static config
 *  only — the reactive spread stays per-element (Hud.setCrosshairSpread). The
 *  neon default is exactly the original reticle. Cheap: a handful of setProperty
 *  calls, invoked only on settings change and at HUD build, never per frame. */
export function applyCrosshairSettings(b: Behavior): void {
  const s = document.documentElement.style;
  const style = b.chStyle ?? CH_DEFAULTS.chStyle;
  const color = CH_COLORS[b.chColor ?? CH_DEFAULTS.chColor] ?? CH_COLORS.cyan;
  const thick = b.chThick ?? CH_DEFAULTS.chThick;
  s.setProperty("--ch-color", color);
  s.setProperty("--ch-size", `${b.chSize ?? CH_DEFAULTS.chSize}px`);
  s.setProperty("--ch-thick", `${thick}px`);
  s.setProperty("--ch-base-gap", `${b.chGap ?? CH_DEFAULTS.chGap}px`);
  s.setProperty("--ch-rings", style === "neon" ? "1" : "0");
  s.setProperty("--ch-arms", style === "dot" ? "0" : "1");
  s.setProperty("--ch-dot", style === "cross" ? "0px" : `${thick + 2}px`);
  s.setProperty(
    "--ch-outline",
    b.chOutline ? "drop-shadow(0 0 1px #000) drop-shadow(0 0 1px #000)" : "none",
  );
}

/** One board row: the shared standings view of a player, plus who they are to
 *  the local client. Already filtered and sorted by `MatchMode.scoreRows()`. */
export interface ScoreRow extends StandingPlayer {
  isMe: boolean;
}

/** The local player's own match summary, shown under the end-of-round board. */
export interface MatchSummary {
  shots: number;
  hits: number;
  damage: number;
  streak: number;
  /** Longest kill distance this match, metres (0 = no kills yet). */
  longShot: number;
  kills: number;
  deaths: number;
}

/** BETWEEN-ROUND MAP VOTE, as the over-screen needs it. Built by MatchMode
 *  from `rules.vote`: the candidate map ids, the running per-option tally, this
 *  client's own pick (-1 = not voted), whether the vote is still open, and the
 *  resolved winner once it closes. */
export interface MapVote {
  options: string[];
  counts: number[];
  myPick: number;
  open: boolean;
  winner: string;
}

/** YOUR MATCH — the local player's own numbers under the end-of-round board.
 *  The standings above answer "who won"; this answers "how did I do", which no
 *  screen in the game answered before. */
function summaryStrip(s: MatchSummary): HTMLElement {
  // accuracy is hitscan-only on both sides (see StatsState.shots) — with no
  // shots fired there is no ratio to show rather than a misleading 0%
  const acc = s.shots > 0 ? `${Math.round((s.hits / s.shots) * 100)}%` : "—";
  const kd = s.deaths > 0 ? (s.kills / s.deaths).toFixed(2) : s.kills.toFixed(2);
  // no kills = no distance; toFixed(0) above ~10 m keeps the tile narrow
  const shot = s.longShot > 0 ? `${s.longShot.toFixed(s.longShot < 10 ? 1 : 0)}m` : "—";
  const stat = (label: string, value: string) =>
    el("div", { class: "mo-stat" },
      el("div", { class: "mo-stat-v", text: value }),
      el("div", { class: "mo-stat-l", text: label }),
    );
  return el("div", { class: "mo-summary" },
    el("div", { class: "mo-summary-t", text: "YOUR MATCH" }),
    el("div", { class: "mo-stats" },
      stat("ACCURACY", acc),
      stat("DAMAGE", String(s.damage)),
      stat("BEST STREAK", String(s.streak)),
      stat("LONGEST KILL", shot),
      stat("K/D", kd),
    ),
  );
}

/** MATCH AWARDS — room-wide superlatives (MVP, DEADEYE, SHARPSHOOTER…), one
 *  medal per standout. Complements YOUR MATCH (your own numbers): this names
 *  who owned each stat, so the round's best aim / longest kill / nastiest streak
 *  gets a line even when that player didn't win. Selection is shared logic
 *  (`computeAwards`); this only draws the result. */
function awardsRow(awards: Award[]): HTMLElement {
  return el("div", { class: "mo-awards" },
    el("div", { class: "mo-awards-t", text: "MATCH AWARDS" }),
    el("div", { class: "mo-medals" },
      ...awards.map((a) =>
        el("div", { class: "mo-medal", title: `${a.label} — ${a.winnerName} (${a.value})` },
          el("div", { class: "mo-medal-ico", text: a.icon }),
          el("div", { class: "mo-medal-l", text: a.label }),
          el("div", { class: "mo-medal-w", text: a.winnerName }),
          el("div", { class: "mo-medal-v", text: a.value }),
          ...(a.detail ? [el("div", { class: "mo-medal-v", text: a.detail })] : []),
        ),
      ),
    ),
  );
}

/** In-match HUD: hp, weapon/ammo, tier ladder, killfeed, banners, scoreboard. */
/** A leading run of emoji (with joiners / variation selectors) + spaces. */
const ANN_ICON_RE = /^((?:\p{Extended_Pictographic}|\p{Regional_Indicator}|[\u2600-\u27BF\u2B00-\u2BFF])(?:[\uFE0F\u200D\u20E3]|\p{Extended_Pictographic})*)\s*/u;

export class Hud {
  readonly root: HTMLElement;
  private hpFill: HTMLElement;
  private hpText: HTMLElement;
  private weaponEl: HTMLElement;
  private ammoEl: HTMLElement;
  private killfeed: HTMLElement;
  private hitmarker: HTMLElement;
  private deathOverlay: HTMLElement;
  private statusEl: HTMLElement;
  private codeEl: HTMLElement;
  private crosshair: HTMLElement;
  private tierText: HTMLElement;
  private ladderBar: HTMLElement;
  private tierNext: HTMLElement;
  private timerEl: HTMLElement;
  private banner: HTMLElement;
  private matchOverEl: HTMLElement;
  private scoreboardEl: HTMLElement;
  private bloodEl: HTMLElement;
  private hitFlashEl: HTMLElement;
  private healFlashEl: HTMLElement;
  private damageDirLayer: HTMLElement;
  private damageNumLayer: HTMLElement;
  private armorWrap: HTMLElement;
  private armorFill: HTMLElement;
  private grenadeEl: HTMLElement;
  private micEl: HTMLButtonElement;
  private announceEl: HTMLElement;
  private announceIco: HTMLElement;
  private announceTxt: HTMLElement;
  private chatLog: HTMLElement;
  private vehiclePromptEl: HTMLElement;
  private vehicleHudEl: HTMLElement;
  private scopeEl: HTMLElement;
  private pingEl: HTMLElement;
  private teamEl: HTMLElement;
  private nemesisEl: HTMLElement;
  private emoteWheelEl: HTMLElement | null = null;
  private emoteSegs: HTMLElement[] = [];
  private lastEmoteFav = -2;
  private lastEmoteSel = -2;
  private chatInput: HTMLInputElement | null = null;
  private announceTimer = 0;
  private hitmarkerTimer = 0;
  private bannerTimer = 0;
  private ladderSegments: HTMLElement[] = [];

  constructor() {
    this.crosshair = el("div", { class: "crosshair" },
      el("div", { class: "ch-dot" }),
      el("div", { class: "ch-arm ch-t" }),
      el("div", { class: "ch-arm ch-b" }),
      el("div", { class: "ch-arm ch-l" }),
      el("div", { class: "ch-arm ch-r" }),
    );
    applyCrosshairSettings(loadBehavior());
    this.hitmarker = el("div", { class: "hitmarker" });
    this.statusEl = el("div", { class: "hud-hint" });
    this.codeEl = el("div", { class: "hud-code" });
    this.timerEl = el("div", { class: "hud-timer" });
    this.hpFill = el("div", { class: "hp-fill" });
    this.hpText = el("div", { class: "hp-text", text: "100" });
    this.weaponEl = el("div", { class: "weapon-name" });
    this.ammoEl = el("div", { class: "ammo" });
    this.killfeed = el("div", { class: "killfeed" });
    this.deathOverlay = el("div", { class: "death-overlay" });
    this.tierText = el("div", { class: "tier-text" });
    this.ladderBar = el("div", { class: "ladder-bar" });
    this.tierNext = el("div", { class: "tier-next" });
    this.banner = el("div", { class: "banner" });
    this.matchOverEl = el("div", { class: "match-over" });
    this.scoreboardEl = el("div", { class: "scoreboard" });
    this.bloodEl = el("div", { class: "blood-overlay" });
    this.hitFlashEl = el("div", { class: "hit-flash" });
    this.healFlashEl = el("div", { class: "heal-flash" });
    this.damageDirLayer = el("div", { class: "dmg-dir-layer" });
    this.damageNumLayer = el("div", { class: "dmg-num-layer" });
    this.armorFill = el("div", { class: "armor-fill" });
    this.armorWrap = el("div", { class: "hud-armor", style: "display:none" }, el("div", { class: "armor-bar" }, this.armorFill));
    this.grenadeEl = el("div", { class: "hud-grenade", style: "display:none" });
    this.micEl = el("button", { class: "hud-mic", title: "Voice mic — click or press M" },
      el("span", { class: "mic-key", text: "M" }),
      el("span", { class: "mic-ico", text: "🎤" }));
    this.micEl.onclick = () => void this.toggleMic();
    this.announceIco = el("span", { class: "ann-ico" });
    this.announceTxt = el("span", { class: "ann-txt" });
    this.announceEl = el("div", { class: "announce" }, this.announceIco, this.announceTxt);
    this.chatLog = el("div", { class: "chat-log" });
    this.vehiclePromptEl = el("div", { class: "vehicle-prompt", style: "display:none" });
    this.vehicleHudEl = el("div", { class: "vehicle-hud", style: "display:none" });
    this.pingEl = el("div", {
      style:
        "position:absolute;top:8px;right:12px;font-size:11px;letter-spacing:0.1em;" +
        "color:#5f6b7a;text-shadow:0 0 4px #000;pointer-events:none",
    });
    this.teamEl = el("div", {
      style: "display:none;position:absolute;top:42px;left:12px;padding:6px 11px;border:2px solid;border-radius:4px;font-weight:900;font-size:13px;letter-spacing:.16em;text-shadow:0 1px 4px #000;box-shadow:0 0 14px currentColor;pointer-events:none",
    });
    this.nemesisEl = el("div", { class: "hud-nemesis" });
    // sniper scope lens: black outside a circle + fine crosshair (CSS only)
    this.scopeEl = el("div", {
      style:
        "display:none;position:absolute;inset:0;pointer-events:none;z-index:5;" +
        "background:" +
        // BIG cross reticle through the whole lens
        "linear-gradient(to right, transparent calc(50% - 1.5px), rgba(20,20,20,0.9) calc(50% - 1.5px), rgba(20,20,20,0.9) calc(50% + 1.5px), transparent calc(50% + 1.5px))," +
        "linear-gradient(to bottom, transparent calc(50% - 1.5px), rgba(20,20,20,0.9) calc(50% - 1.5px), rgba(20,20,20,0.9) calc(50% + 1.5px), transparent calc(50% + 1.5px))," +
        // hard black lens ring — a REAL lunette, not a faint vignette
        "radial-gradient(circle at 50% 50%, transparent 27%, rgba(0,0,0,0.88) 29%, #000 33%)",
    });

    this.root = el(
      "div",
      { class: "hud" },
      this.bloodEl,
      this.hitFlashEl,
      this.healFlashEl,
      this.damageDirLayer,
      this.damageNumLayer,
      this.announceEl,
      this.chatLog,
      this.armorWrap,
      this.grenadeEl,
      this.micEl,
      this.vehiclePromptEl,
      this.vehicleHudEl,
      this.scopeEl,
      this.pingEl,
      this.teamEl,
      this.nemesisEl,
      this.crosshair,
      this.hitmarker,
      this.statusEl,
      this.codeEl,
      this.timerEl,
      el("div", { class: "hud-hp" }, el("div", { class: "hp-bar" }, this.hpFill), this.hpText),
      el("div", { class: "hud-weapon" }, this.weaponEl, this.ammoEl),
      el("div", { class: "hud-tier" }, this.tierText, this.ladderBar, this.tierNext),
      this.killfeed,
      this.banner,
      this.deathOverlay,
      this.matchOverEl,
      this.scoreboardEl,
    );
  }

  /** Rendered team-badge state: -1 hidden, 1 red, 2 blue. Per-frame caller,
   *  so skip identical writes like the other setters (setBlood/setHp/…). */
  private lastTeamKey = 0;
  setTeam(team: number, visible: boolean): void {
    const key = !visible || (team !== 1 && team !== 2) ? -1 : team;
    if (key === this.lastTeamKey) return;
    this.lastTeamKey = key;
    if (key === -1) { this.teamEl.style.display = "none"; return; }
    const red = key === 1;
    this.teamEl.style.display = "block";
    this.teamEl.style.color = red ? "#ff5577" : "#5aa9ff";
    this.teamEl.style.borderColor = red ? "#ff365c" : "#318cff";
    this.teamEl.style.background = red ? "rgba(80,8,20,.78)" : "rgba(5,30,78,.78)";
    this.teamEl.textContent = red ? "RED TEAM" : "BLUE TEAM";
  }

  /** Persistent blood vignette — heavier the lower your HP (0..1 = full..dead). */
  private lastBlood = "";
  setBlood(hpFraction: number): void {
    // no blood above 60% HP; ramps up sharply below that
    const t = Math.max(0, Math.min(1, (0.6 - hpFraction) / 0.6));
    const v = (t * 0.85).toFixed(3);
    if (v === this.lastBlood) return; // per-frame caller — skip identical writes
    this.lastBlood = v;
    this.bloodEl.style.opacity = v;
  }

  /** Brief red splash when you take a hit. */
  hitFlash(): void {
    this.hitFlashEl.classList.remove("show");
    void this.hitFlashEl.offsetWidth;
    this.hitFlashEl.classList.add("show");
  }

  /** Directional damage wedge: angle in radians relative to where you face. */
  showDamageDir(angle: number): void {
    const wedge = el("div", { class: "dmg-dir" });
    // rotate so 0 = top (in front); positive = to the right
    wedge.style.transform = `translate(-50%, -50%) rotate(${angle}rad)`;
    this.damageDirLayer.append(wedge);
    setTimeout(() => wedge.remove(), 900);
  }

  /** Floating damage number near the crosshair when you land a hit. */
  showDamageNumber(amount: number, headshot: boolean): void {
    const n = el("div", {
      class: `dmg-num${headshot ? " head" : ""}`,
      text: headshot ? "HEADSHOT" : String(amount),
    });
    const dx = (Math.random() - 0.5) * 40;
    n.style.left = `calc(50% + ${dx}px)`;
    this.damageNumLayer.append(n);
    setTimeout(() => n.remove(), 750);
  }

  private lastStatus = "";
  setStatus(text: string): void {
    if (text === this.lastStatus) return;
    this.lastStatus = text;
    this.statusEl.textContent = text;
  }

  private lastCode = "";
  setCode(code: string): void {
    if (code === this.lastCode) return;
    this.lastCode = code;
    this.codeEl.textContent = code ? `CODE ${code}` : "";
  }

  private lastTimer = -2;
  private lastSudden = false;
  setTimer(msLeft: number, sudden = false): void {
    if (sudden !== this.lastSudden) { // toggle once, not per frame
      this.lastSudden = sudden;
      this.timerEl.classList.toggle("sudden", sudden);
      this.lastTimer = -1; // force the clock text to re-render with/without the 2× prefix now
    }
    if (!Number.isFinite(msLeft)) {
      if (this.lastTimer !== -1) { this.lastTimer = -1; this.timerEl.textContent = ""; }
      return;
    }
    const s = Math.max(0, Math.ceil(msLeft / 1000));
    if (s === this.lastTimer) return; // per-frame caller — one DOM write per second
    this.lastTimer = s;
    // SUDDEN PURGE: prefix the ticking clock with a 2× so the double-damage
    // window is legible at a glance, not just from the OVERCHARGE HUD chip.
    this.timerEl.textContent = sudden ? `⚡2× ${fmtClock(msLeft)}` : fmtClock(msLeft);
    this.timerEl.classList.toggle("urgent", s <= 30);
  }

  private lastHpShown = -1;
  setHp(hp: number, maxHp = 100): void {
    if (hp === this.lastHpShown && maxHp === this.lastMaxHpShown) return; // per-frame caller
    this.lastHpShown = hp;
    this.lastMaxHpShown = maxHp;
    this.hpFill.style.width = `${Math.min(100, (hp / Math.max(1, maxHp)) * 100)}%`;
    this.hpFill.classList.toggle("low", hp <= 30);
    this.hpText.textContent = maxHp > 100 ? `${hp}/${maxHp}` : String(hp);
  }
  private lastMaxHpShown = 100;

  /** Reactive crosshair: arm gap grows with movement/fire/air spread (px). */
  private lastSpread = -1;
  setCrosshairSpread(px: number): void {
    if (Math.abs(px - this.lastSpread) < 0.25) return;
    this.lastSpread = px;
    this.crosshair.style.setProperty("--ch-gap", `${px.toFixed(1)}px`);
  }

  private lastArmor = -1;
  setArmor(armor: number): void {
    if (armor === this.lastArmor) return; // per-frame caller
    this.lastArmor = armor;
    this.armorWrap.style.display = armor > 0 ? "flex" : "none";
    this.armorFill.style.width = `${armor}%`;
  }

  /** Reflect the shared voice state on the HUD mic button (called each frame
   *  from the match so lobby-started voice shows as ON in game). */
  syncMicUi(on: boolean): void {
    this.micEl.classList.toggle("on", on);
  }

  /** Toggle the voice mic on/off (M key or clicking the button). Default off.
   *  Drives the process-wide voice singleton so state is stable across
   *  lobby↔match. */
  async toggleMic(): Promise<void> {
    // The browser suppresses the mic-permission prompt while the pointer is
    // locked, so release it first (the player clicks to re-lock after).
    if (!voiceChat.on && document.pointerLockElement) document.exitPointerLock();
    this.announce("🎤 …", false);
    const on = await voiceChat.toggle();
    this.syncMicUi(on);
    this.announce(on ? "🎤 MIC ON" : "🎤 MIC OFF", false);
  }

  private killcamEl: HTMLElement | null = null;

  /** Killcam caption while dead ("killed by X — skip"); null hides it. */
  private impScopeEl: HTMLElement | null = null;
  private impReticle: SVGElement | null = null;

  /** IMPERION neon scope: dark vignette lens + glowing reticle that doubles
   *  as the charge meter (cyan → white, ring closes at full charge). Pass
   *  null to hide; charge 0..1 while scoped. */
  setImperionScope(charge: number | null): void {
    if (charge === null) {
      if (this.impScopeEl) { this.impScopeEl.remove(); this.impScopeEl = null; this.impReticle = null; }
      return;
    }
    if (!this.impScopeEl) {
      const el = document.createElement("div");
      el.className = "imp-scope";
      el.style.cssText =
        "position:absolute;inset:0;z-index:40;pointer-events:none;" +
        // circular lens: transparent centre, near-black outside
        "background:radial-gradient(circle at 50% 50%, transparent 31%, rgba(2,4,8,0.55) 36%, rgba(2,4,8,0.97) 44%);";
      // CRT fringe on the lens edge + scanlines inside the glass
      const fringe = document.createElement("div");
      fringe.style.cssText =
        "position:absolute;left:50%;top:50%;width:72vmin;height:72vmin;transform:translate(-50%,-50%);" +
        "border-radius:50%;box-shadow:inset 0 0 22px rgba(0,240,255,0.35), inset 3px 0 6px rgba(255,47,208,0.18), inset -3px 0 6px rgba(0,240,255,0.25);" +
        "background:repeating-linear-gradient(0deg, transparent 0 3px, rgba(0,240,255,0.02) 3px 4px);";
      el.append(fringe);
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("viewBox", "-100 -100 200 200");
      svg.style.cssText = "position:absolute;left:50%;top:50%;width:66vmin;height:66vmin;transform:translate(-50%,-50%);overflow:visible";
      svg.innerHTML =
        '<g id="ret" stroke="#00f0ff" fill="none" stroke-width="0.7" opacity="0.95">' +
        '<line x1="-42" y1="0" x2="-7" y2="0"/><line x1="7" y1="0" x2="42" y2="0"/>' +
        '<line x1="0" y1="-42" x2="0" y2="-7"/><line x1="0" y1="7" x2="0" y2="42"/>' +
        '<circle cx="0" cy="0" r="1.4" fill="#00f0ff" stroke="none"/>' +
        '<circle id="ring" cx="0" cy="0" r="26" stroke-dasharray="5 4" opacity="0.6"/>' +
        // range ticks down the vertical axis, retro-tactical
        '<g stroke-width="0.5" opacity="0.7">' +
        '<line x1="-3" y1="14" x2="3" y2="14"/><line x1="-2" y1="22" x2="2" y2="22"/>' +
        '<line x1="-3" y1="30" x2="3" y2="30"/><line x1="-2" y1="38" x2="2" y2="38"/>' +
        "</g>" +
        '<text x="6" y="15.5" fill="#00f0ff" stroke="none" font-size="4" font-family="monospace" opacity="0.65">50</text>' +
        '<text x="6" y="31.5" fill="#00f0ff" stroke="none" font-size="4" font-family="monospace" opacity="0.65">100</text>' +
        "</g>";
      el.append(svg);
      this.impReticle = svg.querySelector("#ret") as SVGElement;
      this.root.append(el);
      this.impScopeEl = el;
    }
    // the reticle IS the charge meter: cyan → white + ring closing in
    if (this.impReticle) {
      const c = charge >= 0.99 ? "#ffffff" : charge > 0
        ? `rgb(${Math.round(0 + 255 * charge)}, ${Math.round(240 + 15 * charge)}, 255)` : "#00f0ff";
      const pulse = charge > 0 && charge < 0.99 ? 0.75 + 0.25 * Math.sin(performance.now() / 90) : 1;
      this.impReticle.setAttribute("stroke", c);
      this.impReticle.setAttribute("opacity", String(0.95 * pulse));
      const ring = this.impReticle.querySelector("#ring");
      ring?.setAttribute("r", String(26 - 18 * charge));
      ring?.setAttribute("opacity", charge >= 0.99 ? "1" : "0.6");
    }
  }

  private lastKillcamHtml = "";

  /** Killcam caption. `text` is the prefix ("killcam: NAME"); this appends a
   *  server-authoritative "♥N" badge showing the killer's HP at the killing
   *  blow — the "you nearly had them" beat every shooter has — then the skip
   *  hint. `killerHp < 0` = unknown (suicide / vehicle kill), badge omitted.
   *  HTML-escaped + sig-gated so the per-frame while-dead caller writes the DOM
   *  only when the string actually changes. */
  setKillcam(text: string | null, killerHp = -1, killerMaxHp = 100): void {
    if (!text) {
      this.killcamEl?.remove();
      this.killcamEl = null;
      this.lastKillcamHtml = "";
      return;
    }
    if (!this.killcamEl) {
      this.killcamEl = el("div", {
        style:
          "position:absolute;left:50%;bottom:22%;transform:translateX(-50%);" +
          "color:#7ff3ff;font-size:13px;letter-spacing:0.18em;text-transform:uppercase;" +
          "text-shadow:0 0 8px #00f0ff88;pointer-events:none;white-space:nowrap",
      });
      this.root.append(this.killcamEl);
    }
    const esc = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
    let html = esc(text);
    if (killerHp >= 0) {
      if (killerHp === 0) {
        // a trade: your killer went down too (they were at 0 when they got you)
        html += ` <span style="color:#ff9a5a;font-weight:800;text-shadow:0 0 7px #ff9a5aaa">♥0 traded!</span>`;
      } else {
        const frac = killerHp / Math.max(1, killerMaxHp);
        const close = killerHp <= 20 || frac <= 0.25;
        const col = close ? "#ff6b6b" : frac < 0.5 ? "#ffd23d" : "#7dff9b";
        html += ` <span style="color:${col};font-weight:800;text-shadow:0 0 7px ${col}aa">♥${killerHp}</span>`;
        if (close) html += ` <span style="color:#ff9a5a;font-weight:800">so close</span>`;
      }
    }
    html += ` — [space / Ⓧ] skip`;
    if (html === this.lastKillcamHtml) return; // per-frame caller — skip identical writes
    this.lastKillcamHtml = html;
    this.killcamEl.innerHTML = html;
  }

  /** Round-trip latency readout, colored by connection quality. */
  private lastPingSig = "";
  setPing(ms: number, fps?: number, gfxLvl?: number, programs?: number, padInfo?: string): void {
    const v = Math.round(ms);
    const f = fps !== undefined ? Math.round(fps) : -1;
    const sig = `${v}/${f}/${gfxLvl ?? ""}/${programs ?? ""}/${padInfo ?? ""}`;
    if (sig === this.lastPingSig) return;
    this.lastPingSig = sig;
    const diag = (gfxLvl !== undefined && gfxLvl > 0 ? ` · L${gfxLvl}${programs !== undefined ? `·p${programs}` : ""}` : "") +
      (padInfo ? ` · ${padInfo}` : "");
    this.pingEl.textContent = f >= 0 ? `${f} fps · ${v} ms${diag}` : `${v} ms`;
    this.pingEl.style.color = v < 60 ? "#3ddc84" : v < 120 ? "#ffd166" : "#ff5577";
  }

  /** Sniper scope lens overlay (hides the normal crosshair while up). */
  setScope(on: boolean): void {
    this.scopeEl.style.display = on ? "block" : "none";
    this.crosshair.style.display = on ? "none" : "";
  }

  private mobilityEl: HTMLElement | null = null;
  private mobilityKey = "";

  /** Persistent icons for movement gear: 🎒 jetpack (bright while gliding),
   *  🪂 canopy deployed — so you always know what's on your back. */
  setMobility(jetpack: boolean, gliding: boolean, chute: boolean): void {
    const key = `${jetpack}|${gliding}|${chute}`;
    if (key === this.mobilityKey) return;
    this.mobilityKey = key;
    if (!this.mobilityEl) {
      this.mobilityEl = el("div", {
        // bottom-left LANES (see styles.css): hp 1.2rem → armor 2.6rem →
        // mobility 74px → chips 108px → chat input 10.4rem → chat log 12.6rem
        style: "position:absolute;left:14px;bottom:74px;display:flex;gap:8px;font-size:1.5em;pointer-events:none",
      });
      this.root.append(this.mobilityEl);
    }
    // ALWAYS visible on every map: dimmed when inactive, lit when live —
    // one glance tells you what's on your back
    const badge = (active: boolean, hot: boolean, color: string, text: string): HTMLElement =>
      el("span", {
        style:
          `padding:2px 8px;background:rgba(6,10,18,0.7);` +
          `border:1px solid ${hot ? color : active ? "#5a6b7a" : "#232a33"};` +
          `${hot ? "" : active ? "opacity:0.75" : "opacity:0.28"}`,
        text,
      });
    this.mobilityEl.replaceChildren(
      badge(jetpack, jetpack && gliding, "#9dff00", jetpack && gliding ? "🎒 FLYING" : "🎒"),
      badge(chute, chute, "#7ff3ff", chute ? "🪂 GLIDING" : "🪂"),
    );
  }

  setItems(
    grenades: number,
    hasJetpack: boolean,
    rocketAmmo: number,
    imperionAmmo = 0,
    healthShots = 0,
    speedMs = 0,
    dmgMs = 0,
    cloakMs = 0,
    antigravMs = 0,
  ): void {
    // setItems runs EVERY frame. The DOM only shows whole-second boost timers,
    // so bucket the four timers to seconds and bail with ZERO allocation when
    // nothing a player can see has changed. The old guard built a lines[]
    // array, a `secs` closure, ~8 template strings and a join() EVERY frame
    // just to compare — pure render-loop churn (invariant 6). Now the common
    // unchanged frame is nine primitive compares and returns.
    const jp = hasJetpack ? 1 : 0;
    const spd = Math.ceil(Math.max(0, speedMs) / 1000);
    const dmg = Math.ceil(Math.max(0, dmgMs) / 1000);
    const clk = Math.ceil(Math.max(0, cloakMs) / 1000);
    const ag = Math.ceil(Math.max(0, antigravMs) / 1000);
    // The grenade PANEL shows everything but healthShots; guard on exactly its
    // inputs so it rebuilds on precisely the frames the old string-sig did.
    // The chips (health/grenade badges) have their own guard, so setChips still
    // runs every frame — allocation-free, so that costs nothing on a still frame.
    if (grenades !== this.itemsG || jp !== this.itemsJ || rocketAmmo !== this.itemsR ||
        imperionAmmo !== this.itemsI || spd !== this.itemsSpd || dmg !== this.itemsDmg ||
        clk !== this.itemsClk || ag !== this.itemsAg) {
      this.itemsG = grenades; this.itemsJ = jp; this.itemsR = rocketAmmo; this.itemsI = imperionAmmo;
      this.itemsSpd = spd; this.itemsDmg = dmg; this.itemsClk = clk; this.itemsAg = ag;
      const lines: string[] = [];
      if (spd > 0) lines.push(`⚡ OVERDRIVE +45% speed — ${spd}s`);
      if (dmg > 0) lines.push(`☠ OVERCHARGE 2x damage — ${dmg}s`);
      if (clk > 0) lines.push(`👻 GHOST CLOAK hidden — ${clk}s`);
      if (ag > 0) lines.push(`🌌 ANTIGRAVITY moon gravity — ${ag}s`);
      if (rocketAmmo > 0) lines.push(`🚀 ROCKETS ×${rocketAmmo}  [fire / RT]`);
      if (imperionAmmo > 0) lines.push(`⚡ IMPERION charges ×${imperionAmmo}`);
      if (hasJetpack) lines.push(`🪂 JETPACK  [2× space/Ⓐ fly · 1× stop · shift/L3 boost]`);
      if (grenades > 0) lines.push(`🧨 GRENADES ×${grenades}  [2 / E / RB]`);
      this.grenadeEl.style.display = lines.length ? "block" : "none";
      this.grenadeEl.innerHTML = lines.join("<br>");
    }
    this.setChips(healthShots, grenades);
  }

  private itemsG = -1;
  private itemsJ = -1;
  private itemsR = -1;
  private itemsI = -1;
  private itemsSpd = -1;
  private itemsDmg = -1;
  private itemsClk = -1;
  private itemsAg = -1;
  private chipsEl: HTMLElement | null = null;
  private lastChipsHealth = -1;
  private lastChipsGren = -1;
  /** MOBILE: tapping a consumable chip uses it (wired by match to the input). */
  onChipTap: ((act: "heal" | "grenade") => void) | null = null;

  /** Big unmissable consumable badges above the HP bar (clear of the mobility
   *  icons row): a glowing green ✚ heal chip and an amber 🧨 grenade chip. */
  private setChips(healthShots: number, grenades: number): void {
    if (healthShots === this.lastChipsHealth && grenades === this.lastChipsGren) return;
    this.lastChipsHealth = healthShots;
    this.lastChipsGren = grenades;
    if (!this.chipsEl) {
      this.chipsEl = el("div", {
        // bottom:108px — a REAL gap above the mobility icons (74px + ~28px
        // tall); at 118 over 88 the health chip sat right on the jetpack icon.
        // pointer-events AUTO: on touch the chips ARE the heal/grenade buttons
        style: "position:absolute;left:14px;bottom:108px;display:none;gap:10px;pointer-events:auto;z-index:5;touch-action:none",
      });
      // MOBILE: tapping a chip uses it (touch only — desktop stays keyboard)
      this.chipsEl.addEventListener("touchstart", (e) => {
        const act = (e.target as HTMLElement).closest?.("[data-act]")?.getAttribute("data-act");
        if (act === "heal" || act === "grenade") {
          e.preventDefault();
          this.onChipTap?.(act);
        }
      }, { passive: false });
      this.root.append(this.chipsEl);
    }
    const chip = (color: string, glow: string, icon: string, n: number, hint: string, act: string): string =>
      `<div data-act="${act}" style="display:flex;align-items:center;gap:8px;padding:5px 12px;` +
      `background:rgba(6,12,10,0.8);border:2px solid ${color};border-radius:6px;` +
      `box-shadow:0 0 12px ${glow};color:${color};font-size:1.15em;text-shadow:0 0 8px ${color}">` +
      `<span style="font-size:1.4em;font-weight:bold">${icon}</span>` +
      `<span style="font-weight:bold">×${n}</span>` +
      `<span style="font-size:0.6em;opacity:0.85">${hint}</span></div>`;
    const parts: string[] = [];
    if (healthShots > 0) parts.push(chip("#37d67a", "rgba(55,214,122,0.6)", "✚", healthShots, "heal&nbsp;[1/↑]", "heal"));
    if (grenades > 0) parts.push(chip("#ffb63d", "rgba(255,182,61,0.55)", "🧨", grenades, "[2/E/RB]", "grenade"));
    if (!parts.length) { this.chipsEl.style.display = "none"; return; }
    this.chipsEl.style.display = "flex";
    this.chipsEl.innerHTML = parts.join("");
  }

  /** Green (health) / blue (armor) pickup flash. */
  flashHeal(armor: boolean): void {
    this.healFlashEl.style.background = armor
      ? "radial-gradient(ellipse at center, rgba(63,169,245,0) 40%, rgba(63,169,245,0.4) 100%)"
      : "radial-gradient(ellipse at center, rgba(55,214,122,0) 40%, rgba(55,214,122,0.4) 100%)";
    this.healFlashEl.classList.remove("show");
    void this.healFlashEl.offsetWidth;
    this.healFlashEl.classList.add("show");
  }

  /** Multi-kill / killstreak announce banner. */
  announce(label: string, streak: boolean): void {
    // split a leading emoji run into its own icon tile ("🛸🎶 X SUMMONED…")
    const m = ANN_ICON_RE.exec(label);
    const icon = m ? m[1] : "";
    const text = m ? label.slice(m[0].length) : label;
    this.announceIco.textContent = icon;
    this.announceTxt.textContent = text;
    this.announceTxt.style.whiteSpace = "pre-line"; // multi-line labels (e.g. the hunt teaser)
    const long = text.length > 30 || text.includes("\n");
    // restart the animation even when an announce is already on screen
    this.announceEl.className = "announce";
    void this.announceEl.offsetWidth;
    this.announceEl.className = `announce show ${streak ? "streak" : "multi"}${long ? " long" : ""}`;
    // reading time scales with LENGTH — and the pop animation must last as
    // long as the timer (it was fixed at 1.8s, fading long texts out early)
    const ms = Math.min(6500, Math.max(1800, 900 + label.length * 55));
    this.announceEl.style.animationDuration = `${ms}ms`;
    clearTimeout(this.announceTimer);
    this.announceTimer = window.setTimeout(() => (this.announceEl.className = "announce"), ms);
  }

  private lastNemesisSig = "none";
  private nemesisRevengeUntil = 0;
  private nemesisTimer = 0;
  /** Persistent ⚔ NEMESIS grudge chip. `name===null` hides it. Per-frame
   *  caller, so it only touches the DOM when the rival or their kill-count
   *  changes (invariant 6). A fresh kill-count pops a red pulse; while a REVENGE
   *  flash is playing (see `nemesisRevenge`) it holds so the payoff isn't cut. */
  setNemesis(name: string | null, count: number): void {
    if (performance.now() < this.nemesisRevengeUntil) return; // let the payoff play out
    const sig = name ? `n:${name}:${count}` : "none";
    if (sig === this.lastNemesisSig) return;
    const bumped = name != null && this.lastNemesisSig.startsWith("n:"); // count/name changed while active
    this.lastNemesisSig = sig;
    if (!name) { this.nemesisEl.className = "hud-nemesis"; return; }
    this.nemesisEl.replaceChildren(
      el("span", { class: "nm-ico", text: "⚔" }),
      el("span", { class: "nm-body" },
        el("span", { class: "nm-title", text: "NEMESIS" }),
        el("span", { class: "nm-name", text: name })),
      el("span", { class: "nm-count", text: `×${count}` }),
    );
    this.nemesisEl.className = "hud-nemesis show";
    if (bumped) { void this.nemesisEl.offsetWidth; this.nemesisEl.classList.add("bump"); } // re-pulse on a new kill
  }

  /** REVENGE payoff: flash the chip green for ~1.6s, then let `setNemesis`
   *  hide it (nemesisId has cleared server-side by then). */
  nemesisRevenge(name: string): void {
    this.nemesisRevengeUntil = performance.now() + 1600;
    this.lastNemesisSig = "revenge";
    this.nemesisEl.replaceChildren(
      el("span", { class: "nm-ico", text: "💀" }),
      el("span", { class: "nm-body" },
        el("span", { class: "nm-title", text: "REVENGE" }),
        el("span", { class: "nm-name", text: `${name} down` })),
      el("span", { class: "nm-count", text: "✓" }),
    );
    this.nemesisEl.className = "hud-nemesis show revenge";
    clearTimeout(this.nemesisTimer);
    this.nemesisTimer = window.setTimeout(() => {
      this.nemesisEl.className = "hud-nemesis";
      this.lastNemesisSig = "none";
    }, 1600);
  }

  private tauntEl: HTMLElement | null = null;
  private tauntTimer = 0;
  /** Pop a big moquerie emote so the local player sees their own taunt. */
  showTaunt(kind: number): void {
    const emotes = ["👋😝", "🤷😜", "💪😎", "👉😂", "🕺🤪"];
    if (!this.tauntEl) {
      this.tauntEl = el("div", {
        style: "position:absolute;left:50%;top:38%;transform:translate(-50%,-50%);" +
          "font-size:5.5rem;pointer-events:none;filter:drop-shadow(0 0 12px #00f0ff);" +
          "transition:transform .18s ease-out,opacity .3s;opacity:0;z-index:6",
      });
      this.root.append(this.tauntEl);
    }
    this.tauntEl.textContent = emotes[Math.max(0, Math.min(emotes.length - 1, Math.floor(kind)))];
    // pop in
    this.tauntEl.style.opacity = "1";
    this.tauntEl.style.transform = "translate(-50%,-50%) scale(1.15) rotate(-6deg)";
    clearTimeout(this.tauntTimer);
    this.tauntTimer = window.setTimeout(() => {
      if (this.tauntEl) { this.tauntEl.style.opacity = "0"; this.tauntEl.style.transform = "translate(-50%,-50%) scale(0.8)"; }
    }, 800);
  }

  /** EMOTE WHEEL — the five taunts laid out radially. Index = server tauntKind,
   *  so the tile the player highlights is exactly the taunt that plays (the
   *  poses live in avatar.ts applyTauntPose). Built lazily on first open. */
  private buildEmoteWheel(): void {
    if (this.emoteWheelEl) return;
    const emotes = [
      { ico: "👋", label: "TAUNT" },
      { ico: "🤷", label: "SHRUG" },
      { ico: "💪", label: "FLEX" },
      { ico: "😂", label: "LAUGH" },
      { ico: "🕺", label: "DANCE" },
    ];
    const wheel = el("div", { class: "emote-wheel" });
    const n = emotes.length;
    this.emoteSegs = emotes.map((e, i) => {
      const ang = (i / n) * 360; // 0 = top, clockwise
      const seg = el("div", {
        class: "emote-seg",
        // push each tile out along its spoke, then un-rotate it upright
        style: `transform:translate(-50%,-50%) rotate(${ang}deg) translateY(-104px) rotate(${-ang}deg)`,
      },
        el("div", { class: "emote-ico", text: e.ico }),
        el("div", { class: "emote-label", text: e.label }),
      );
      wheel.append(seg);
      return seg;
    });
    wheel.append(el("div", { class: "emote-hub", text: "EMOTE" }));
    this.emoteWheelEl = wheel;
    this.root.append(wheel);
  }

  /** Show the radial emote picker (ArrowDown held). */
  openEmoteWheel(): void {
    this.buildEmoteWheel();
    this.lastEmoteSel = -2;
    this.lastEmoteFav = -2;
    this.setEmoteWheelSel(-1);
    this.emoteWheelEl!.classList.add("show");
  }
  /** Highlight the segment the pointer is steering toward (-1 = none/centre). */
  setEmoteWheelSel(sel: number): void {
    if (sel === this.lastEmoteSel) return;
    this.lastEmoteSel = sel;
    for (let i = 0; i < this.emoteSegs.length; i++) {
      this.emoteSegs[i].classList.toggle("sel", i === sel);
    }
  }
  /** Star the favourite tile (the quick-tap default) so the player can see at a
   *  glance which taunt a fast press plays. Class-only (a CSS ★ badge), never a
   *  transform — the spoke placement stays inline (invariant 6). */
  setEmoteWheelFav(fav: number): void {
    if (fav === this.lastEmoteFav) return;
    this.lastEmoteFav = fav;
    for (let i = 0; i < this.emoteSegs.length; i++) {
      this.emoteSegs[i].classList.toggle("fav", i === fav);
    }
  }
  /** Hide the wheel. */
  closeEmoteWheel(): void {
    this.emoteWheelEl?.classList.remove("show");
  }

  private lastVehiclePrompt: string | null = "";
  setVehiclePrompt(text: string | null): void {
    if (text === this.lastVehiclePrompt) return; // per-frame caller
    this.lastVehiclePrompt = text;
    this.vehiclePromptEl.textContent = text ?? "";
    this.vehiclePromptEl.style.display = text ? "block" : "none";
  }

  private lastVehicleHudSig = "";
  setVehicleHud(name: string | null, hp: number, maxHp: number, bombs?: number): void {
    // per-frame caller — rebuilding these DOM nodes at 60 Hz while driving was
    // measurable jank; only touch the DOM when something actually changed
    const sig = `${name}/${hp}/${maxHp}/${bombs ?? "-"}`;
    if (sig === this.lastVehicleHudSig) return;
    this.lastVehicleHudSig = sig;
    if (!name) { this.vehicleHudEl.style.display = "none"; return; }
    this.vehicleHudEl.style.display = "block";
    const rows = [
      el("div", { class: "vh-name", text: name }),
      el("div", { text: `HULL ${hp} / ${maxHp}` }),
    ];
    if (bombs !== undefined) {
      rows.push(el("div", { style: "color:#ffb02e;font-weight:bold", text: `💣 BOMBS ×${bombs}` }));
      rows.push(el("div", { class: "hint", text: bombs > 0 ? "drop bomb: fire / RT" : "grab bombs from the boxes!" }));
    } else {
      rows.push(el("div", { class: "hint", text: "F to exit" }));
    }
    this.vehicleHudEl.replaceChildren(...rows);
  }

  /** Open the chat input; calls onSend(text) on Enter. */
  openChat(onSend: (text: string) => void, onClose: () => void): void {
    if (this.chatInput) return;
    const input = el("input", { class: "chat-input", type: "text", maxlength: "120", placeholder: "say something…" }) as HTMLInputElement;
    this.chatInput = input;
    this.root.append(input);
    input.focus();
    const finish = (send: boolean): void => {
      if (send && input.value.trim()) onSend(input.value.trim());
      input.remove();
      this.chatInput = null;
      onClose();
    };
    input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") finish(true);
      else if (e.key === "Escape") finish(false);
    });
    input.addEventListener("blur", () => finish(false));
  }

  get chatOpen(): boolean {
    return this.chatInput !== null;
  }

  addChat(from: string, text: string): void {
    const line = el("div", { class: "chat-line" },
      el("span", { class: "chat-from", text: `${from}: ` }),
      document.createTextNode(text),
    );
    this.chatLog.append(line);
    while (this.chatLog.children.length > 8) this.chatLog.firstChild?.remove();
    setTimeout(() => line.classList.add("fade"), 8000);
    setTimeout(() => line.remove(), 10000);
  }

  /** Player-to-player chat: side popup, visible but clear of the crosshair. */
  private chatPopEl: HTMLElement | null = null;
  private chatPopTimer: ReturnType<typeof setTimeout> | null = null;
  chatPopup(from: string, text: string): void {
    this.chatPopEl?.remove();
    if (this.chatPopTimer) clearTimeout(this.chatPopTimer);
    const pop = el("div", {
      style: "position:absolute;right:1.2rem;top:29%;max-width:min(420px,34vw);text-align:left;z-index:8;" +
        "background:linear-gradient(90deg,rgba(5,8,14,0.96),rgba(5,8,14,0.74));" +
        "border-left:5px solid #00f0ff;border-top:1px solid rgba(0,240,255,0.55);border-bottom:1px solid rgba(0,240,255,0.45);" +
        "box-shadow:0 0 22px rgba(0,240,255,0.45),inset 0 0 18px rgba(0,240,255,0.08);" +
        "padding:0.72em 1.05em;color:#eaf3ff;font-size:1.05em;line-height:1.25;pointer-events:none;" +
        "text-shadow:0 0 8px rgba(0,240,255,0.65);word-break:break-word",
    },
      el("span", { text: `${from}: `, style: "color:#00f0ff;font-weight:bold" }),
      document.createTextNode(text),
    );
    this.root.append(pop);
    this.chatPopEl = pop;
    this.chatPopTimer = setTimeout(() => { pop.remove(); this.chatPopEl = null; }, 4200);
  }

  /** Big center-screen pulse while the weapon is recharging. */
  private reloadBannerEl: HTMLElement | null = null;
  setReloadingBanner(on: boolean): void {
    if (on && !this.reloadBannerEl) {
      this.reloadBannerEl = el("div", {
        text: "⟳ RECHARGING…",
        style: "position:absolute;left:50%;top:58%;transform:translateX(-50%);color:#00f0ff;" +
          "font-size:1.15em;letter-spacing:0.18em;text-shadow:0 0 12px #00f0ff;pointer-events:none;" +
          "animation:np-blink 0.55s ease-in-out infinite alternate",
      });
      this.root.append(this.reloadBannerEl);
    } else if (!on && this.reloadBannerEl) {
      this.reloadBannerEl.remove();
      this.reloadBannerEl = null;
    }
  }

  private lastWeaponSig = "";
  setWeapon(weaponId: string, ammo: number, reloading: boolean): void {
    // sig-guarded like every other per-frame setter (called at 60Hz from the
    // frame loop — unguarded it rewrote both text nodes every frame)
    const sig = `${weaponId}/${ammo}/${reloading ? 1 : 0}`;
    if (sig === this.lastWeaponSig) return;
    this.lastWeaponSig = sig;
    const cfg = WEAPONS[weaponId];
    this.weaponEl.textContent = cfg ? `${cfg.displayName} · ${weaponType(cfg)} · ${weaponRarity(cfg)}` : weaponId;
    this.weaponEl.title = cfg
      ? `${cfg.displayName}\n${weaponType(cfg)} · ${weaponRarity(cfg)}\nDamage: ${cfg.pellets ? `${cfg.damage} x ${cfg.pellets}` : cfg.damage}\nAmmo: ${cfg.melee ? "none" : `${cfg.magSize} magazine`}\n${weaponTraits(cfg).join(", ")}`
      : weaponId;
    if (cfg?.melee) this.ammoEl.textContent = "∞";
    else this.ammoEl.textContent = reloading ? "RELOADING…" : `${ammo} / ${cfg?.magSize ?? "?"}`;
  }

  /** Gun Game ladder indicator: current rung out of N + weapon name. */
  private starsEl: HTMLElement | null = null;
  private lastStarsSig = "";
  /** Bob-omb Blast score banner: your stars, the win target, the leader, and
   *  a compact live standings column (place · name · stars). */
  setArenaStars(mine: number | null, target: number, leaderName: string, leaderStars: number, standings: { name: string; stars: number; isMe: boolean; level?: number }[] = [], timed = false): void {
    const sig = mine === null ? "off" : `${timed ? "T" : "R"}/${mine}/${target}/${leaderName}/${leaderStars}/${standings.map((s) => `${s.name}:${s.stars}:${s.level ?? 0}${s.isMe ? "*" : ""}`).join(",")}`;
    if (sig === this.lastStarsSig) return;
    this.lastStarsSig = sig;
    if (!this.starsEl) {
      this.starsEl = el("div", {
        style: "position:absolute;top:44px;left:50%;transform:translateX(-50%);text-align:center;" +
          "pointer-events:none;z-index:5;font-size:1.3em;color:#ffd23d;text-shadow:0 0 10px #ffb02e;",
      });
      this.root.append(this.starsEl);
    }
    if (mine === null) { this.starsEl.style.display = "none"; return; }
    this.starsEl.style.display = "block";
    // BOTH repeats must be clamped — stars can be stolen below zero, and a
    // negative count throws RangeError straight out of setArenaStars, which
    // freezes this HUD element for the rest of the match.
    const held = Math.max(0, mine);
    const stars = "★".repeat(held) + "☆".repeat(Math.max(0, target - held));
    const esc = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const board = standings.length > 1
      ? `<div style="margin-top:0.3em;font-size:0.55em;letter-spacing:0.1em;text-align:left;display:inline-block">` +
        standings.map((s, i) =>
          `<div style="color:${s.isMe ? "#00e5ff" : "#cfe0ff"}">${ordinal(i + 1)} ${esc(s.name)}` +
          // account level (0 = guest/bot) — a quiet prefix, not a shout
          `${s.level ? ` <span style="opacity:0.6;font-size:0.9em">L${s.level}</span>` : ""} ${s.stars}★</div>`,
        ).join("") + `</div>`
      : "";
    // Banner reads as what the mode ACTUALLY is: a TIMED most-stars battle when
    // a round clock is running (the win goes to the leader when it expires, with
    // the star cap as an early KO for a blowout), or a plain race to the cap
    // when there is no timer. Before this it always said "FIRST TO N STARS",
    // which framed the timed MK battle as an open-ended sprint.
    const headline = timed
      ? `MOST STARS WINS · ${target}★ ends it early`
      : `FIRST TO ${target} STARS`;
    this.starsEl.innerHTML =
      `<div style="font-size:1.4em;letter-spacing:2px">${stars}</div>` +
      `<div style="font-size:0.6em;color:#cfe0ff;letter-spacing:0.15em">${headline}` +
      (leaderName ? ` · leader ${esc(leaderName)} ${leaderStars}★` : "") + `</div>` + board;
  }

  private ladderRankEl: HTMLElement | null = null;
  private lastLadderRankSig = "";
  private lastMyLadderPlace = 0;
  /** Gun Game / Burnout LIVE ladder standings — a compact top-center race board
   *  (place · name · rung) so the arms race is glanceable mid-fight. The flagship
   *  ladder modes used to show only YOUR OWN rung on the bottom ladder bar; you
   *  could never see whether you were winning, who was ahead, or who was one kill
   *  from the final weapon. Mirrors arena's setArenaStars board and reuses the
   *  same sorted `scoreRows()` data. `null` (or <2 players) hides it. */
  setLadderRank(entries: { place: number; name: string; tier: number; isMe: boolean; final: boolean; gap: number }[] | null): void {
    const sig = !entries || entries.length === 0 ? "off"
      : entries.map((e) => `${e.place}:${e.name}:${e.tier}:${e.isMe ? 1 : 0}:${e.final ? 1 : 0}:${e.gap}`).join(",");
    if (sig === this.lastLadderRankSig) return; // per-frame caller — skip identical writes
    this.lastLadderRankSig = sig;
    if (!this.ladderRankEl) {
      this.ladderRankEl = el("div", { class: "ladder-rank" });
      this.root.append(this.ladderRankEl);
    }
    const box = this.ladderRankEl;
    if (sig === "off") { box.style.display = "none"; this.lastMyLadderPlace = 0; return; }
    box.style.display = "block";
    // flash MY row once when my place actually changes (not on every tier tick)
    const myPlace = entries!.find((e) => e.isMe)?.place ?? 0;
    const flashMe = this.lastMyLadderPlace !== 0 && myPlace !== 0 && myPlace !== this.lastMyLadderPlace;
    this.lastMyLadderPlace = myPlace;
    const esc = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
    box.innerHTML =
      `<div class="lr-title">LADDER</div>` +
      entries!.map((e) => {
        const cls = `lr-row${e.isMe ? " me" : ""}${e.place === 1 ? " lead" : ""}${e.final ? " final" : ""}${e.isMe && flashMe ? " place-change" : ""}`;
        const tag = e.final ? "⚔ FINAL" : `T${e.tier + 1}`;
        const gap = e.gap > 0 ? `<span class="lr-gap up">+${e.gap}</span>`
          : e.gap < 0 ? `<span class="lr-gap dn">−${-e.gap}</span>`
          : `<span class="lr-gap"></span>`;
        return `<div class="${cls}">` +
          `<span class="lr-place">${ordinal(e.place)}</span>` +
          `<span class="lr-name">${esc(e.name)}</span>` +
          gap +
          `<span class="lr-tier">${tag}</span></div>`;
      }).join("");
  }

  /** "CLICK TO AIM" — shown whenever the game runs without pointer lock. */
  private clickAimEl: HTMLElement | null = null;
  private lastClickAim = false;
  setClickToAim(on: boolean): void {
    if (on === this.lastClickAim) return;
    this.lastClickAim = on;
    if (!on) { this.clickAimEl?.remove(); this.clickAimEl = null; return; }
    this.clickAimEl = el("div", { class: "click-aim", text: "🖱 CLICK TO AIM" });
    this.root.append(this.clickAimEl);
  }

  /** DEAD SPECTATE split screen: divider + who's on the right half. */
  private deadSpecEl: HTMLElement | null = null;
  private lastDeadSpecName: string | null = null;
  setDeadSpectate(name: string | null): void {
    if (name === this.lastDeadSpecName) return;
    this.lastDeadSpecName = name;
    if (!name) { this.deadSpecEl?.remove(); this.deadSpecEl = null; return; }
    if (!this.deadSpecEl) {
      this.deadSpecEl = el("div", { class: "dead-spec" },
        el("div", { class: "dead-spec-divider" }),
        el("div", { class: "dead-spec-mine", text: "YOUR CAM" }),
        el("div", { class: "dead-spec-label" }),
      );
      this.root.append(this.deadSpecEl);
    }
    const label = this.deadSpecEl.querySelector(".dead-spec-label") as HTMLElement;
    label.textContent = `📺 ${name} — [space / Ⓐ] switch`;
  }

  private purgeEl: HTMLElement | null = null;
  private lastPurgeSig = "";
  /** PURGE HUD: round number, zombies left / next-round countdown, points. */
  setPurge(round: number, left: number, points: number, nextInMs: number, lives = 0): void {
    const sig = round < 0 ? "off" : `${round}/${left}/${points}/${Math.ceil(Math.max(0, nextInMs) / 1000)}/${lives}`;
    if (sig === this.lastPurgeSig) return;
    this.lastPurgeSig = sig;
    if (!this.purgeEl) {
      this.purgeEl = el("div", {
        style: "position:absolute;top:44px;left:50%;transform:translateX(-50%);text-align:center;" +
          "pointer-events:none;z-index:5;color:#ff4d4d;text-shadow:0 0 12px #a00;",
      });
      this.root.append(this.purgeEl);
    }
    if (round < 0) { this.purgeEl.style.display = "none"; return; }
    this.purgeEl.style.display = "block";
    const sub = nextInMs > 0
      ? `next horde in ${Math.ceil(nextInMs / 1000)}s`
      : left > 0 ? `${left} zombie${left === 1 ? "" : "s"} left` : "";
    this.purgeEl.innerHTML =
      `<div style="font-size:1.9em;font-weight:bold;letter-spacing:0.2em">${round > 0 ? "ROUND " + round : "GET READY"}</div>` +
      `<div style="font-size:0.85em;color:#ffb9b9">${sub}</div>` +
      `<div style="font-size:1.05em;color:#ffd166;margin-top:2px">◎ ${points}&nbsp;&nbsp;<span style="color:#ff6b7d">${"❤".repeat(Math.max(0, lives))}</span></div>`;
  }

  private burnEl: HTMLElement | null = null;
  // allocation-free dirty-check (this runs every frame in burnout) — the DOM
  // only shows fuse/freeze to 0.1s and heat/re-arm to 1s, so bucket to those
  // and compare primitives instead of building a `sig` string each frame.
  private burnFuseB = -2;
  private burnFreezeB = -1;
  private burnHeatB = -1;
  private burnTier = -1;
  private burnTarget = -1;
  private burnLead = -1;
  private burnStoke = -1;
  private burnStokeCdB = -1;
  private burnLeadName = " ";
  /** BURNOUT: fires the STOKE wager (wired by MatchMode; touch taps the chip). */
  onStoke: (() => void) | null = null;
  /**
   * BURNOUT HUD: the two clocks that drive the mode. Normally it's the countdown
   * to the next global heat step (everyone climbs a rung) plus the frag race.
   * Once you're on the final tier it becomes the fuse — the only number that
   * matters — and turns red. A fuse burning BELOW the final tier is a STOKE
   * wager (self-lit): same red urgency, its own payout/forfeit line.
   * Pass fuseMs < 0 to hide the whole readout (any other mode).
   * `canStoke` shows the wager hint chip (tappable on touch — pointer events
   * are enabled on the chip only, the rest of the readout stays click-through).
   * `stokeCdMs > 0` shows the chip DISABLED with a re-arm countdown instead —
   * the server rejects wagers inside stokeCooldownMs and used to do so with
   * zero feedback (the chip looked armed and ate the press).
   */
  setBurnout(fuseMs: number, freezeMs: number, heatMs: number, tier: number, target: number, leadName: string, lead: number, canStoke = false, stokeCdMs = 0): void {
    const lit = fuseMs > 0;
    // -1 is the "hidden" bucket (fuseMs < 0); real fuse buckets are >= 0
    const fuseB = fuseMs < 0 ? -1 : (lit ? Math.ceil(fuseMs / 100) : 0);
    const freezeB = Math.ceil(freezeMs / 100);
    const heatB = Math.ceil(heatMs / 1000);
    const stokeB = canStoke ? 1 : 0;
    const cdB = Math.ceil(stokeCdMs / 1000);
    if (fuseB === this.burnFuseB && freezeB === this.burnFreezeB && heatB === this.burnHeatB &&
        tier === this.burnTier && target === this.burnTarget && lead === this.burnLead &&
        stokeB === this.burnStoke && cdB === this.burnStokeCdB && leadName === this.burnLeadName) return;
    this.burnFuseB = fuseB; this.burnFreezeB = freezeB; this.burnHeatB = heatB;
    this.burnTier = tier; this.burnTarget = target; this.burnLead = lead;
    this.burnStoke = stokeB; this.burnStokeCdB = cdB; this.burnLeadName = leadName;
    if (!this.burnEl) {
      this.burnEl = el("div", {
        style: "position:absolute;top:44px;left:50%;transform:translateX(-50%);text-align:center;" +
          "pointer-events:none;z-index:5;font-size:0.8rem;letter-spacing:0.16em",
      });
      this.root.append(this.burnEl);
    }
    if (fuseMs < 0) { this.burnEl.style.display = "none"; return; }
    this.burnEl.style.display = "block";
    const race = `RUNG ${tier}/${target}${leadName ? ` &nbsp;·&nbsp; LEADER: ${leadName} ${lead}/${target}` : ""}`;
    if (freezeMs > 0) {
      this.burnEl.style.color = "#67d9ff";
      this.burnEl.style.textShadow = "0 0 14px rgba(103,217,255,0.9)";
      this.burnEl.innerHTML =
        `<div style="font-size:1.5em;font-weight:bold">❄️ HEAT FROZEN — ${(freezeMs / 1000).toFixed(1)}s</div>` +
        `<div style="font-size:0.9em;opacity:0.8;margin-top:2px">${race}</div>`;
      return;
    }
    // real payout of a cash-in: promote() clamps at the ladder top, so near
    // the knife the wager pays less than the flat +3 — advertise the truth
    // (tier/target are 1-based here; the knife rung is index target-1).
    const stokeRungs = Math.min(BURNOUT.stokeBonusTiers + 1, target - tier);
    if (lit) {
      const stoked = tier < target; // a burning fuse below the top rung is a self-lit wager
      this.burnEl.style.color = "#ff4d3d";
      this.burnEl.style.textShadow = "0 0 14px rgba(255,77,61,0.9)";
      this.burnEl.innerHTML = stoked
        ? `<div style="font-size:1.7em;font-weight:bold">🔥 STOKED — ${(fuseMs / 1000).toFixed(1)}s</div>` +
          `<div style="font-size:0.95em">KILL NOW: +${stokeRungs} RUNGS &nbsp;·&nbsp; FAIL: BURN OUT</div>` +
          `<div style="font-size:0.9em;opacity:0.75;margin-top:2px">${race}</div>`
        : `<div style="font-size:1.7em;font-weight:bold">🔥 OVERHEATING — ${(fuseMs / 1000).toFixed(1)}s</div>` +
          `<div style="font-size:0.95em">KILL WITH THE BURNOUT KNIFE TO WIN</div>` +
          `<div style="font-size:0.9em;opacity:0.75;margin-top:2px">${race}</div>`;
    } else {
      this.burnEl.style.color = "#ffb02e";
      this.burnEl.style.textShadow = "0 0 10px rgba(255,176,46,0.7)";
      this.burnEl.innerHTML =
        `<div style="font-size:1.05em">🔥 HEAT RISES IN ${Math.ceil(heatMs / 1000)}s</div>` +
        `<div style="font-size:0.9em;opacity:0.8;margin-top:2px">${race}</div>` +
        (canStoke
          ? `<div id="np-stoke" style="pointer-events:auto;display:inline-block;margin-top:3px;padding:2px 8px;` +
            `border:1px solid rgba(255,176,46,0.5);border-radius:3px;font-size:0.8em;opacity:0.75">` +
            `[G / tap] STOKE — kill in ${Math.round(BURNOUT.fuseMs / 1000)}s = +${stokeRungs} rungs · fail = burn</div>`
          : stokeCdMs > 0
            // cooling down: same chip, visibly disabled (no pointer events, no
            // id — a tap or G does nothing and the countdown says why)
            ? `<div style="display:inline-block;margin-top:3px;padding:2px 8px;` +
              `border:1px solid rgba(255,176,46,0.25);border-radius:3px;font-size:0.8em;opacity:0.4">` +
              `STOKE RE-ARMS IN ${Math.ceil(stokeCdMs / 1000)}s</div>`
            : "");
      // touch has no G key: the chip itself is the button (rebuilt at most
      // ~1/s by the sig gate, so re-wiring the handler here is cheap)
      const chip = this.burnEl.querySelector("#np-stoke") as HTMLElement | null;
      if (chip) chip.ontouchstart = (e) => { e.preventDefault(); this.onStoke?.(); };
    }
  }

  /**
   * Shared builder for the left-column "rules in force" chips — the round
   * MODIFIER, the LOADOUT perk, and the practice SPRINT card. They were three
   * byte-for-byte copies of the same lazy-create-then-restyle DOM dance,
   * differing only in their `top:` offset and the text/colour each computes.
   * `existing` is the cached element (null before first show); returns the
   * element so the caller re-caches it. `text === null` hides the chip. Only
   * ever runs when the caller's per-frame signature guard has already decided
   * the chip changed, so the no-op path allocates nothing (invariant 6).
   */
  private rulesChip(
    existing: HTMLElement | null,
    top: number,
    text: string | null,
    color: string,
  ): HTMLElement | null {
    if (text === null) {
      if (existing) existing.style.display = "none";
      return existing;
    }
    let chip = existing;
    if (!chip) {
      chip = el("div", {
        style: `position:absolute;top:${top}px;left:18px;pointer-events:none;z-index:5;` +
          "font-size:0.72rem;letter-spacing:0.18em;font-weight:bold;padding:3px 9px;" +
          "border-radius:3px;background:rgba(6,8,14,0.55)",
      });
      this.root.append(chip);
    }
    chip.style.display = "block";
    chip.style.color = color;
    chip.style.border = `1px solid ${color}`;
    chip.style.textShadow = `0 0 10px ${color}`;
    chip.textContent = text;
    return chip;
  }

  private modEl: HTMLElement | null = null;
  private lastModSig = "";
  /**
   * ROUND MODIFIER chip: a persistent reminder of which mutator is running.
   * Deliberately quiet and out of the way (top-left, under the tier readout) —
   * it has to stay legible for a whole match without competing with the mode's
   * own objective panel. Pass null for STANDARD rules, which shows nothing.
   */
  setModifier(label: string | null, color?: string): void {
    const sig = label ? `${label}|${color ?? ""}` : "off";
    if (sig === this.lastModSig) return; // per-frame caller
    this.lastModSig = sig;
    this.modEl = this.rulesChip(this.modEl, 96, label ? `⚡ ${label}` : null, color ?? "#9fb0c8");
  }

  private loadEl: HTMLElement | null = null;
  private lastLoadSig = "";
  /**
   * LOADOUT PERK chip: which passive YOU are running this life. Sits directly
   * under the round-modifier chip and is styled the same, because they read as
   * the same kind of fact ("rules in force right now") — the difference is that
   * the modifier is the room's and this one is yours. Pass null for no perk.
   *
   * Deliberately shows the ACTIVE perk (replicated `p.loadout`), not the one
   * picked in the lobby: a change queued mid-life only lands on respawn, and
   * the chip is how the player finds that out without being told.
   */
  setLoadout(label: string | null, icon?: string, color?: string): void {
    const sig = label ? `${label}|${color ?? ""}` : "off";
    if (sig === this.lastLoadSig) return; // per-frame caller
    this.lastLoadSig = sig;
    this.loadEl = this.rulesChip(this.loadEl, 122, label ? `${icon ?? ""} ${label}` : null, color ?? "#9fb0c8");
  }

  private sprintEl: HTMLElement | null = null;
  private lastSprintSig = "";
  /**
   * PRACTICE LADDER SPRINT card (practice-range rooms only). Sits under the
   * loadout chip in the "rules in force" stack. `runMs === null` hides it
   * (not a practice room); `runMs < 0` = clock idle (armed by the first
   * kill); `runMs >= 0` = a run is live, show the ticking clock. The display
   * clock is client-local — the authoritative time arrives with SprintDone.
   */
  setSprint(runMs: number | null, bestMs: number): void {
    const sig = runMs === null ? "off" : `${runMs < 0 ? -1 : Math.floor(runMs / 100)}/${bestMs}`;
    if (sig === this.lastSprintSig) return; // per-frame caller
    this.lastSprintSig = sig;
    if (runMs === null) {
      this.sprintEl = this.rulesChip(this.sprintEl, 148, null, "#9fb0c8");
      return;
    }
    const live = runMs >= 0;
    const color = live ? "#ffd166" : "#9fb0c8";
    const pb = bestMs > 0 ? ` · PB ${fmtStopwatch(bestMs)}` : "";
    const text = live
      ? `🏁 SPRINT ${fmtStopwatch(runMs)}${pb}`
      : `🏁 SPRINT — first kill starts the clock${pb}`;
    this.sprintEl = this.rulesChip(this.sprintEl, 148, text, color);
  }

  private lastTierSig = "";
  /**
   * Gun Game ladder readout: current rung + weapon, the segment bar, and — in
   * the per-player ladder modes (gungame / burnout) — a preview of what the
   * next kill promotes you to, so you always know what you are climbing toward.
   * `nextName` is the display name of the tier+1 weapon (null hides the preview
   * line: non-laddered modes, or the top rung). `finalCallout` swaps the
   * preview for the "one knife kill wins" banner on the final (knife) rung.
   */
  setTier(tier: number, total: number, weaponId: string, nextName: string | null = null, finalCallout = false): void {
    const sig = `${tier}/${total}/${weaponId}/${nextName ?? ""}/${finalCallout ? 1 : 0}`;
    if (sig === this.lastTierSig) return; // per-frame caller — classList churn adds up
    this.lastTierSig = sig;
    if (this.ladderSegments.length !== total) {
      this.ladderBar.replaceChildren();
      this.ladderSegments = [];
      for (let i = 0; i < total; i++) {
        const seg = el("div", { class: "ladder-seg" });
        this.ladderSegments.push(seg);
        this.ladderBar.append(seg);
      }
    }
    this.ladderSegments.forEach((seg, i) => {
      seg.classList.toggle("done", i < tier);
      seg.classList.toggle("current", i === tier);
      seg.classList.toggle("final", i === total - 1);
    });
    const name = WEAPONS[weaponId]?.displayName ?? weaponId;
    this.tierText.textContent = `TIER ${tier + 1}/${total} — ${name}`;
    // Next-weapon preview / final-rung callout
    if (finalCallout) {
      this.tierNext.className = "tier-next final";
      this.tierNext.textContent = "⚔ FINAL WEAPON — ONE KNIFE KILL WINS";
    } else if (nextName) {
      this.tierNext.className = "tier-next";
      this.tierNext.innerHTML =
        `<span class="tn-arrow">▲</span>NEXT · <span class="tn-name">${nextName.toUpperCase()}</span>`;
    } else {
      this.tierNext.className = "tier-next";
      this.tierNext.textContent = "";
    }
  }

  /** Big center flash: PROMOTED → <weapon> / DEMOTED! */
  flash(kind: "promoted" | "demoted" | "win" | "danger", text: string): void {
    // `danger` = a world event that hurts EVERYONE (THE RING): bigger, red,
    // pulsing, multi-line and held longer — the plain announce line was too
    // small to explain why the whole lobby was suddenly losing HP.
    const ms = kind === "danger" ? 4800 : 2200;
    this.banner.textContent = text;
    this.banner.style.whiteSpace = kind === "danger" ? "pre-line" : "";
    this.banner.className = "banner";
    void this.banner.offsetWidth; // restart the pop if one is already showing
    this.banner.className = `banner show ${kind}`;
    clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => {
      this.banner.className = "banner";
    }, ms);
  }

  showHitmarker(killed: boolean): void {
    this.hitmarker.classList.remove("show", "kill");
    void this.hitmarker.offsetWidth;
    this.hitmarker.classList.add("show");
    if (killed) this.hitmarker.classList.add("kill");
    clearTimeout(this.hitmarkerTimer);
    this.hitmarkerTimer = window.setTimeout(
      () => this.hitmarker.classList.remove("show", "kill"),
      200,
    );
  }

  addKillfeed(text: string, kind: "kill" | "melee" | "tier" = "kill"): void {
    const entry = el("div", { class: `kf-entry kf-${kind}`, text });
    this.killfeed.prepend(entry);
    while (this.killfeed.children.length > 6) this.killfeed.lastChild?.remove();
    setTimeout(() => entry.remove(), 6000);
  }

  private lastDeadSig = "";
  /** FART BLASTER debuff: a translucent green haze over the whole view for
   *  `ms` — fades in/out over 300 ms via CSS, never blocks input or reading
   *  the game (alpha stays well under blinding). Re-hits restart the clock. */
  private fartEl: HTMLElement | null = null;
  private fartTimer: ReturnType<typeof setTimeout> | null = null;
  fartOverlay(ms: number): void {
    if (!this.fartEl) {
      this.fartEl = el("div", { style:
        "position:fixed;inset:0;pointer-events:none;z-index:99990;opacity:0;" +
        "transition:opacity 0.3s ease;background:" +
        "radial-gradient(ellipse at center, rgba(57,255,92,0.18) 0%, rgba(31,168,54,0.42) 78%, rgba(20,120,40,0.55) 100%)" });
      this.root.append(this.fartEl);
    }
    // force a style flush so a re-hit mid-fade restarts the fade-in cleanly
    this.fartEl.getBoundingClientRect();
    this.fartEl.style.opacity = "1";
    if (this.fartTimer) clearTimeout(this.fartTimer);
    this.fartTimer = setTimeout(() => { if (this.fartEl) this.fartEl.style.opacity = "0"; }, Math.max(300, ms - 300));
  }

  /** Hose victim reaction: underwater blur/tint while a sperm or water stream is
   *  actively hitting you. Lazily created, re-hits restart the fade. */
  private hoseEl: HTMLElement | null = null;
  private hoseTimer: ReturnType<typeof setTimeout> | null = null;
  hoseOverlay(ms: number, intensity = 0): void {
    const t = Math.max(0, Math.min(1, intensity));
    if (!this.hoseEl) {
      this.hoseEl = el("div", { style:
        "position:fixed;inset:0;pointer-events:none;z-index:99990;opacity:0;" +
        "transition:opacity 0.3s ease;backdrop-filter:blur(4px) saturate(1.25);-webkit-backdrop-filter:blur(4px) saturate(1.25);" +
        "background:" +
        "radial-gradient(ellipse at center, rgba(70,210,255,0.14) 0%, rgba(18,130,170,0.36) 72%, rgba(4,55,82,0.56) 100%)," +
        "repeating-linear-gradient(76deg, transparent 0 110px, rgba(170,235,255,0.045) 110px 150px, transparent 150px 300px)" });
      this.root.append(this.hoseEl);
    }
    this.hoseEl.getBoundingClientRect();
    const blur = 4 + t * 8;
    const sat = 1.25 + t * 0.45;
    this.hoseEl.style.backdropFilter = `blur(${blur.toFixed(1)}px) saturate(${sat.toFixed(2)})`;
    this.hoseEl.style.setProperty("-webkit-backdrop-filter", `blur(${blur.toFixed(1)}px) saturate(${sat.toFixed(2)})`);
    this.hoseEl.style.opacity = String(0.72 + t * 0.28);
    if (this.hoseTimer) clearTimeout(this.hoseTimer);
    this.hoseTimer = setTimeout(() => { if (this.hoseEl) this.hoseEl.style.opacity = "0"; }, Math.max(300, ms - 300));
  }

  spermOverlay(ms: number): void { this.hoseOverlay(ms); }

  /** PENIS GUN reservoir: a Mario-Sunshine-style vertical tank on the right,
   *  shown only while holding the powerup. `frac` is 0..1. Lazily created so the
   *  Hud constructor stays untouched (mirrors setMobility). */
  private reservoirEl: HTMLElement | null = null;
  private reservoirFill: HTMLElement | null = null;
  private lastReservoir = -2;
  setReservoir(frac: number, holding: boolean): void {
    if (!holding) {
      if (this.reservoirEl && this.reservoirEl.style.display !== "none") this.reservoirEl.style.display = "none";
      this.lastReservoir = -2;
      return;
    }
    if (!this.reservoirEl) {
      this.reservoirFill = el("div", { class: "reservoir-fill" });
      this.reservoirEl = el("div", { class: "reservoir" },
        el("div", { class: "reservoir-bar" }, this.reservoirFill),
        el("div", { class: "reservoir-label", text: "💦" }));
      this.root.append(this.reservoirEl);
    }
    if (this.reservoirEl.style.display !== "flex") this.reservoirEl.style.display = "flex";
    const pct = Math.max(0, Math.min(100, Math.round(frac * 100)));
    if (pct === this.lastReservoir) return;
    this.lastReservoir = pct;
    if (this.reservoirFill) this.reservoirFill.style.height = `${pct}%`;
  }

  setDead(dead: boolean, secondsLeft = 0): void {
    // ROYALE passes Infinity: no respawn is coming — don't count down a lie
    const finalDeath = !Number.isFinite(secondsLeft);
    const sig = dead ? (finalDeath ? "df" : `d${Math.max(0, secondsLeft).toFixed(1)}`) : "a";
    if (sig === this.lastDeadSig) return; // per-frame caller, 10Hz value
    this.lastDeadSig = sig;
    if (dead) {
      this.deathOverlay.textContent = finalDeath
        ? "ELIMINATED — spectating the survivors"
        : `ELIMINATED — respawn in ${Math.max(0, secondsLeft).toFixed(1)}s`;
      this.deathOverlay.classList.add("show");
      this.crosshair.style.display = "none";
    } else {
      this.deathOverlay.classList.remove("show");
      this.crosshair.style.display = "";
    }
  }

  /** Content signature for the two boards below. BOTH are called every frame
   *  (the scoreboard while Tab is held, the summary for the whole "over"
   *  window), and rebuilding their DOM per frame is a layout invalidation at
   *  60-144fps. Accumulating into one string calls each column's `value()`
   *  exactly once per frame; the cells are only rebuilt when it changes. */
  private rowsSig(mode: string, cols: readonly StandingColumn[], rows: readonly ScoreRow[]): string {
    let sig = mode;
    for (const r of rows) {
      sig += `|${r.username}`;
      for (const c of cols) sig += `:${c.value(r)}`;
    }
    return sig;
  }

  private lastMatchOverSig = "";
  /** Host-only: skip the countdown, back to the lobby now (wired by match). */
  onSkipOver: (() => void) | null = null;
  /** Host-only: one-click REMATCH — restart the next round now, on the voted
   *  map, without the lobby round-trip (wired by match → ClientMsg.Rematch). */
  onRematch: (() => void) | null = null;
  /** Cast/change my next-map vote (wired by match → ClientMsg.VoteMap). */
  onMapVote: ((index: number) => void) | null = null;
  /** Cached 2D map thumbnails keyed by map id — drawn once, reused across every
   *  over-screen rebuild (the vote tally rebuilds the board on each new vote,
   *  and redrawing a canvas per rebuild would be wasteful). */
  private voteThumbs = new Map<string, HTMLCanvasElement>();
  setMatchOver(
    mode: string,
    winnerName: string | null,
    isMe: boolean,
    rows?: ScoreRow[],
    secondsLeft?: number,
    isHost = false,
    summary?: MatchSummary | null,
    vote?: MapVote | null,
    awards?: Award[] | null,
    canRematch = false,
  ): void {
    if (winnerName === null) {
      this.matchOverEl.classList.remove("show");
      this.lastMatchOverSig = "";
      return;
    }
    const cols = compactColumns(mode);
    const voteSig = vote && vote.options.length
      ? `${vote.open ? 1 : 0}|${vote.myPick}|${vote.winner}|${vote.options.join(",")}|${vote.counts.join(",")}`
      : "";
    const awardsSig = awards && awards.length
      ? awards.map((a) => `${a.id}:${a.winnerId}:${a.value}:${a.detail ?? ""}`).join(",")
      : "";
    const sig = `${winnerName}|${isMe ? 1 : 0}|${isHost ? 1 : 0}|${canRematch ? 1 : 0}|${secondsLeft ?? -1}|` +
      `${summary ? `${summary.shots}/${summary.hits}/${summary.damage}/${summary.streak}/${summary.longShot}` : ""}|` +
      `${voteSig}|${awardsSig}|` +
      this.rowsSig(mode, cols, rows ?? []);
    if (sig === this.lastMatchOverSig) return;
    this.lastMatchOverSig = sig;
    const board = rows && rows.length
      ? el("div", { class: "mo-board" },
          el("div", { class: "mo-row mo-head" },
            el("span", { text: "#" }), el("span", { text: "PLAYER" }),
            ...cols.map((c) => el("span", { text: c.label })),
          ),
          ...rows.map((r, i) =>
            el("div", { class: `mo-row${r.isMe ? " mo-me" : ""}${i === 0 ? " mo-win" : ""}` },
              el("span", { text: String(i + 1) }),
              el("span", { text: r.username }),
              ...cols.map((c) => el("span", { text: c.value(r) })),
            ),
          ),
        )
      : null;
    // the grid tracks are mode-dependent (arena has 3 stat columns, gungame 4
    // including a wide WEAPON), so they come from the standings rule rather
    // than a hardcoded CSS declaration that every mode has to share
    if (board) board.style.setProperty("--cols", `1.6em 1fr ${cols.map((c) => c.width).join(" ")}`);
    const live = secondsLeft !== undefined && secondsLeft > 0;
    // Host gets a true one-click rematch (restart now, on the voted map) plus a
    // way to the lobby to change setup; the auto-return timer is the third path.
    // Custom-map / practice rooms can't rematch (beginMatch reloads a built-in
    // map), so there the single button keeps its old "to the lobby now" job.
    const hint = live
      ? isHost
        ? (canRematch
            ? `PLAY AGAIN restarts now on the winning map · or open the room lobby to change setup · auto-lobby in ${secondsLeft}s`
            : `back to the room lobby in ${secondsLeft}s — same settings, restart from there`)
        : `host can restart now · the room returns to the lobby in ${secondsLeft}s`
      : "returning to the room lobby…";
    const rematchBtn = isHost && live && canRematch
      ? el("button", { class: "primary", text: "▶ PLAY AGAIN  [Enter]" })
      : null;
    if (rematchBtn) rematchBtn.onclick = () => this.onRematch?.();
    const lobbyBtn = isHost && live
      ? el("button", { class: canRematch ? "" : "primary", text: canRematch ? "⚙ ROOM LOBBY" : "⟳ NEXT ROUND NOW  [Enter]" })
      : null;
    if (lobbyBtn) lobbyBtn.onclick = () => this.onSkipOver?.();
    const btnRow = rematchBtn || lobbyBtn
      ? el("div", { class: "mo-btns" }, ...(rematchBtn ? [rematchBtn] : []), ...(lobbyBtn ? [lobbyBtn] : []))
      : null;
    const voteBoard = vote && vote.options.length >= 2 ? this.buildVoteBoard(vote) : null;
    this.matchOverEl.replaceChildren(
      el("div", { class: "mo-title", text: isMe ? "VICTORY" : "PURGED" }),
      el("div", { class: "mo-sub", text: `${winnerName} wins the round` }),
      ...(board ? [board] : []),
      ...(awards && awards.length ? [awardsRow(awards)] : []),
      ...(summary ? [summaryStrip(summary)] : []),
      ...(voteBoard ? [voteBoard] : []),
      ...(btnRow ? [btnRow] : []),
      el("div", { class: "mo-hint", text: hint }),
    );
    this.matchOverEl.classList.add("show");
  }

  /** VOTE NEXT MAP — a row of candidate map tiles under the standings. Any
   *  player can click while the vote is open; the leader and my own pick are
   *  highlighted, and once the vote resolves the winner is framed. */
  private buildVoteBoard(vote: MapVote): HTMLElement {
    const max = vote.counts.length ? Math.max(...vote.counts) : 0;
    const tiles = vote.options.map((id, i) => {
      let thumb = this.voteThumbs.get(id);
      if (!thumb) {
        thumb = document.createElement("canvas");
        thumb.width = 128; thumb.height = 96; thumb.className = "mo-vote-thumb";
        const m = MAPS[id];
        if (m) drawMapPreview(thumb, m);
        this.voteThumbs.set(id, thumb);
      }
      const name = (MAPS[id]?.name ?? id).replace("🎲 ", "");
      const count = vote.counts[i] ?? 0;
      const mine = vote.myPick === i;
      const winner = !vote.open && vote.winner === id;
      const leader = vote.open && count > 0 && count === max;
      const tile = el("div", {
        class: `mo-vote-tile${mine ? " mine" : ""}${winner ? " winner" : ""}${leader ? " leader" : ""}`,
        title: name,
      },
        thumb,
        el("div", { class: "mo-vote-name", text: name }),
        el("div", { class: "mo-vote-count", text: count > 0 ? String(count) : "" }),
      );
      if (vote.open) { tile.classList.add("clickable"); tile.onclick = () => this.onMapVote?.(i); }
      return tile;
    });
    const title = vote.open
      ? (vote.myPick >= 0 ? "VOTE NEXT MAP — change any time" : "VOTE NEXT MAP")
      : `NEXT MAP: ${(MAPS[vote.winner]?.name ?? "").replace("🎲 ", "") || "—"}`;
    return el("div", { class: "mo-vote" },
      el("div", { class: "mo-vote-t", text: title }),
      el("div", { class: "mo-vote-row" }, ...tiles),
    );
  }

  private lastScoreboardSig = "";
  setScoreboard(mode: string, rows: ScoreRow[] | null): void {
    if (!rows) {
      this.scoreboardEl.classList.remove("show");
      this.lastScoreboardSig = "";
      return;
    }
    const st = standingsFor(mode);
    const sig = this.rowsSig(mode, st.columns, rows);
    if (sig === this.lastScoreboardSig) { this.scoreboardEl.classList.add("show"); return; }
    this.lastScoreboardSig = sig;
    const head = el("div", { class: "sb-row sb-head" },
      el("span", { text: "PLAYER" }),
      ...st.columns.map((c) => el("span", { text: c.label })),
    );
    const body = rows.map((r) =>
      el("div", { class: `sb-row${r.isMe ? " sb-me" : ""}` },
        el("span", { text: r.username }),
        ...st.columns.map((c) => el("span", { text: c.value(r) })),
      ),
    );
    // custom properties inherit, so one write on the container drives every
    // row's `grid-template-columns: var(--cols)`
    this.scoreboardEl.style.setProperty("--cols", `1fr ${st.columns.map((c) => c.width).join(" ")}`);
    this.scoreboardEl.replaceChildren(
      el("div", { class: "sb-title", text: st.title }),
      head,
      ...body,
    );
    this.scoreboardEl.classList.add("show");
  }

  dispose(): void {
    // NB: do NOT stop the mic here — the voice singleton owns it and it must
    // survive the match→lobby transition (stable voice across screens).
    this.root.remove();
  }
}
